/**
 * Helpers para resolver o `tipo_programa` do usuário logado.
 *
 * O `tipo_programa` mora no JWT ou no localStorage("user") e representa o programa da empresa:
 *   - 0 → TCE (Estágio)
 *   - 1 → TCA (Aprendizagem) — fluxo padrão, sem etapa extra do estudante
 *   - 2 → TCA Empregador (Aprendizagem CIEEMG Empregador)
 *
 * Para `0` e `2`, o fluxo do Acompanhamento ganha uma etapa extra
 * "Aguardando Preenchimento do Estagiário" (para 0) ou
 * "Aguardando Preenchimento do Aprendiz" (para 2) entre "Cadastro inicial" e
 * "Validação do preenchimento".
 */

import { jwtDecode } from "jwt-decode";

export const PROGRAMA_TCE = 0;
export const PROGRAMA_TCA = 1;
export const PROGRAMA_TCA_EMPREGADOR = 2;

/** Tipos de programa que exigem etapa "Preenchimento do Estudante/Aprendiz". */
export const PROGRAMA_REQUER_ESTUDANTE = new Set([
  PROGRAMA_TCE,
  PROGRAMA_TCA_EMPREGADOR,
]);

/**
 * Lê o `tipo_programa` do usuário logado a partir do contexto de navegação,
 * localStorage("user") ou JWT.
 * Prioriza explicitamente quando o usuário está no módulo TCE (0).
 */
export function obterTipoProgramaExplicitoUsuario() {
  if (typeof window === "undefined") return null;

  // 0. Referrer da navegação anterior (ex: acabou de vir de /tce)
  try {
    if (typeof document !== "undefined" && document.referrer) {
      const ref = document.referrer.toLowerCase();
      if (ref.includes("/tce")) return PROGRAMA_TCE;
      if (ref.includes("/tca-empregador")) return PROGRAMA_TCA_EMPREGADOR;
      if (ref.includes("/tca")) return PROGRAMA_TCA;
    }
  } catch (_) {}

  // 1. Contexto explícito na aba/sessão ativa (sessionStorage)
  try {
    const moduloSessao = window.sessionStorage.getItem("modulo_ativo");
    if (moduloSessao === "tce" || moduloSessao === "0") return PROGRAMA_TCE;
    if (moduloSessao === "tca-empregador" || moduloSessao === "2") return PROGRAMA_TCA_EMPREGADOR;
    if (moduloSessao === "tca" || moduloSessao === "1") return PROGRAMA_TCA;
  } catch (_) {}

  // 2. Parâmetro na URL atual (?origem=tce, ?programa=tce, etc.)
  try {
    const params = new URLSearchParams(window.location.search);
    const origemUrl = (params.get("origem") || params.get("programa") || "").toLowerCase();
    if (origemUrl === "tce" || params.get("tipo_programa") === "0") return PROGRAMA_TCE;
    if (origemUrl === "tca-empregador" || params.get("tipo_programa") === "2") return PROGRAMA_TCA_EMPREGADOR;
    if (origemUrl === "tca" || params.get("tipo_programa") === "1") return PROGRAMA_TCA;
  } catch (_) {}

  // 3. Objeto user salvo no localStorage (fonte autoritativa da empresa/estudante)
  try {
    const rawUser = window.localStorage.getItem("user");
    if (rawUser) {
      const u = JSON.parse(rawUser);
      if (u?.tipo_programa !== null && u?.tipo_programa !== undefined && u?.tipo_programa !== "") {
        const tp = Number(u.tipo_programa);
        if (Number.isFinite(tp) && (tp === 0 || tp === 1 || tp === 2)) return tp;
      }
    }
  } catch (_) {}

  // 4. Token JWT (fonte autoritativa do backend)
  const token = window.localStorage.getItem("token");
  if (token) {
    try {
      const decoded = jwtDecode(token);
      const bruto = decoded?.tipo_programa;
      if (bruto !== null && bruto !== undefined && bruto !== "") {
        const numero = Number(bruto);
        if (Number.isFinite(numero) && (numero === 0 || numero === 1 || numero === 2)) {
          return numero;
        }
      }
    } catch (_) {}
  }

  // 5. Fallback de navegação persistida no localStorage
  try {
    const moduloPersistido =
      window.localStorage.getItem("ultimo_modulo_acessado") ||
      window.localStorage.getItem("origem_modulo");
    if (moduloPersistido === "tce" || moduloPersistido === "0") return PROGRAMA_TCE;
    if (moduloPersistido === "tca-empregador" || moduloPersistido === "2") return PROGRAMA_TCA_EMPREGADOR;
    if (moduloPersistido === "tca" || moduloPersistido === "1") return PROGRAMA_TCA;
  } catch (_) {}

  return null;
}

export function obterTipoProgramaUsuarioLogado() {
  if (typeof window === "undefined") return PROGRAMA_TCA;
  const explicito = obterTipoProgramaExplicitoUsuario();
  return explicito !== null ? explicito : PROGRAMA_TCA_EMPREGADOR;
}

/**
 * Retorna o rótulo do indivíduo (Estagiário ou Aprendiz) de acordo com o programa.
 */
export function obterRotuloPessoaPorPrograma(tipoPrograma) {
  return Number(tipoPrograma) === PROGRAMA_TCE ? "Estagiário:" : "Aprendiz:";
}

/**
 * Retorna o nome da modalidade do indivíduo de acordo com o programa.
 */
export function obterNomePessoaPorPrograma(tipoPrograma) {
  return Number(tipoPrograma) === PROGRAMA_TCE ? "Estagiário" : "Aprendiz";
}

/**
 * Retorna o texto padrão da etapa 2 de preenchimento de acordo com o programa.
 */
export function obterLabelPreenchimentoEstudante(tipoPrograma) {
  return Number(tipoPrograma) === PROGRAMA_TCE
    ? "Aguardando Preenchimento do Estagiário"
    : "Aguardando Preenchimento do Aprendiz";
}

/**
 * Indica se o programa informado exige a etapa extra do estudante
 * no stepper do Acompanhamento.
 */
export function programaRequerEtapaEstudante(tipoPrograma) {
  return PROGRAMA_REQUER_ESTUDANTE.has(Number(tipoPrograma));
}

/**
 * Resolve o rótulo do programa a partir do item ou documento.
 *
 *   0 → TCE        (Estágio)
 *   1 → TCA        (Aprendizagem)
 *   2 → TCA Emp.   (Aprendizagem)
 */
export function resolverIdentificacaoPrograma(item, documento) {
  // 1) Identificação direta por origem de rota
  const origem = String(
    item?._origem ||
    item?.raw?._origem ||
    item?.origem ||
    item?.tipo_contrato ||
    ""
  ).toLowerCase();

  if (origem === "tce" || origem === "estagio" || item?.tce_id != null) {
    return { tipo: "ESTAGIO", rotulo: "Estágio", classe: "acomp-tp-estagio" };
  }
  if (origem === "tca_empregador" || origem === "tca-empregador" || item?.tca_empregador_id != null) {
    return { tipo: "APRENDIZAGEM", rotulo: "Aprendizagem", classe: "acomp-tp-aprendizagem" };
  }
  if (origem === "tca") {
    return { tipo: "APRENDIZAGEM", rotulo: "Aprendizagem", classe: "acomp-tp-aprendizagem" };
  }

  // 2) Fonte autoritativa: `tipo_programa` no registro.
  const bruto =
    item?.tipo_programa ??
    item?.tipoPrograma ??
    item?.raw?.tipo_programa ??
    item?.raw?.tipoPrograma;

  if (bruto !== undefined && bruto !== null && bruto !== "") {
    const tp = Number(bruto);
    if (Number.isFinite(tp)) {
      if (tp === PROGRAMA_TCE) {
        return { tipo: "ESTAGIO", rotulo: "Estágio", classe: "acomp-tp-estagio" };
      }
      if (tp === PROGRAMA_TCA || tp === PROGRAMA_TCA_EMPREGADOR) {
        return {
          tipo: "APRENDIZAGEM",
          rotulo: "Aprendizagem",
          classe: "acomp-tp-aprendizagem",
        };
      }
      return null;
    }
  }

  // 3) Contexto do usuário logado se for TCE
  const tpLogado = obterTipoProgramaUsuarioLogado();
  if (tpLogado === PROGRAMA_TCE) {
    return { tipo: "ESTAGIO", rotulo: "Estágio", classe: "acomp-tp-estagio" };
  }

  // 4) Se não temos nenhuma pista, retorna null (não chuta pelo CNPJ pois tanto Estágio quanto Aprendizagem usam CNPJ 14 dígitos)
  return null;
}

